import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, staticUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EncounterCard } from "@/components/EncounterCard";
import { StarRating } from "@/components/StarRating";
import { AutoLinkedText } from "@/components/AutoLinkedText";
import { CommentsSection } from "@/components/social/CommentsSection";
import { VoteButtons } from "@/components/social/VoteButtons";
import { AddToCollectionButton } from "@/components/AddToCollectionButton";
import { Eye, Heart, ShieldCheck, MapPin, Calendar, User, Tag, MessageSquare, Loader2, Send, Dna, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const FALLBACK = "https://static.prod-images.emergentagent.com/jobs/fd94d242-06a5-4af0-93ba-0c141315848a/images/9bfbab4035d0ff1ff074c7aafc01237c0a827a32f863078809cf41a80484f384.png";

export default function EncounterDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [enc, setEnc] = useState(null);
    const [similar, setSimilar] = useState([]);
    const [matchCount, setMatchCount] = useState(null);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState("");
    const [posting, setPosting] = useState(false);

    const reload = () => {
        setLoading(true);
        // Each call gets its own .catch() so a 404 on the primary encounter
        // (e.g. guest visiting a pending case) doesn't reject Promise.all
        // and bubble an unhandled axios error. We treat enc=null as "not found".
        Promise.all([
            api.get(`/encounters/${id}`).then((r) => setEnc(r.data)).catch(() => setEnc(null)),
            api.get(`/encounters/${id}/similar`).then((r) => setSimilar(r.data.items || [])).catch(() => setSimilar([])),
            api.get(`/encounters/${id}/matches`, { params: { limit: 1 } }).then((r) => setMatchCount(r.data.total)).catch(() => setMatchCount(0)),
            api.get(`/encounters/${id}/comments`).then((r) => setComments(r.data.items || [])).catch(() => setComments([])),
        ]).finally(() => setLoading(false));
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

    // Fire view_case once per case ID — only the public ID/category go to trackers,
    // never the report text or witness description.
    useEffect(() => {
        if (enc && enc.id) {
            track("view_case", {
                case_id: enc.id,
                category: enc.source_type || enc.encounter_type || "",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enc?.id]);

    const toggleLike = async () => {
        if (!user) return toast.error("Sign in to like encounters");
        try {
            const { data } = await api.post(`/encounters/${id}/like`);
            setEnc((e) => ({ ...e, _user_liked: data.liked, like_count: (e.like_count || 0) + (data.liked ? 1 : -1) }));
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const rate = async (stars) => {
        if (!user) return toast.error("Sign in to rate");
        try {
            await api.post(`/encounters/${id}/rate`, { stars });
            toast.success(`You rated ${stars} ★`);
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const submitCredibility = async (score) => {
        if (!user) return toast.error("Sign in to score credibility");
        try {
            await api.post(`/encounters/${id}/credibility`, { score });
            toast.success("Credibility recorded");
            reload();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const postComment = async (e) => {
        e.preventDefault();
        if (!user) return toast.error("Sign in to comment");
        if (!comment.trim()) return;
        setPosting(true);
        try {
            const { data } = await api.post(`/encounters/${id}/comments`, { body: comment });
            setComments((cs) => [data, ...cs]);
            setComment("");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setPosting(false); }
    };

    if (loading) return <div className="ok-container py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    if (!enc) return <div className="ok-container py-20 text-center">Encounter not found.</div>;

    const img = staticUrl(enc.sketch_url) || FALLBACK;
    const isMine = user && enc.author_id && enc.author_id === user.id;
    const status = enc.status;
    const showStatusBanner = isMine && status && status !== "approved" && status !== "published";

    return (
        <div className="ok-container py-10 max-w-5xl mx-auto" data-testid="encounter-detail-page">
            {/* Pending review banner — only the author sees this */}
            {showStatusBanner && (
                <div className="ok-card p-4 mb-6 flex items-start gap-3" style={{ borderColor: "var(--ok-accent)" }} data-testid="encounter-status-banner">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: "var(--ok-accent)" }} />
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">
                            {status === "pending_review" && "Awaiting Moderator Review"}
                            {status === "needs_changes" && "Changes Requested"}
                            {status === "rejected" && "Submission Rejected"}
                        </div>
                        <div className="font-display text-lg mt-0.5">
                            {status === "pending_review" && "Your report has been received and is in the moderator queue."}
                            {status === "needs_changes" && "A moderator has asked for updates before this can go live."}
                            {status === "rejected" && "This submission did not meet the archive's standards."}
                        </div>
                        <div className="text-xs opacity-70 mt-1 leading-relaxed">
                            {status === "pending_review" && "It will appear in the public archive once approved. You can find all your submissions in "}
                            {status === "needs_changes" && "Review the moderator notes below and re-submit. "}
                            {status === "rejected" && "Reach out via the contact page if you believe this is in error. "}
                            <Link to="/my-submissions" className="ok-link" data-testid="status-banner-mine-link">My Submissions</Link>.
                        </div>
                        {enc.admin_notes && (
                            <div className="mt-3 ok-card p-3 text-sm">
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-1">Moderator note</div>
                                <div className="opacity-90 italic">{enc.admin_notes}</div>
                            </div>
                        )}
                        {enc.rejection_reason && (
                            <div className="mt-3 ok-card p-3 text-sm">
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-1">Rejection reason</div>
                                <div className="opacity-90 italic">{enc.rejection_reason}</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Hero image */}
            <div className="ok-card overflow-hidden mb-2" data-testid="detail-hero">
                <div className="aspect-[16/8] overflow-hidden">
                    <img src={img} alt={enc.title} className="w-full h-full object-cover" />
                </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-6 px-1">
                <p className="text-[11px] opacity-50 italic font-mono uppercase tracking-widest" data-testid="reconstruction-caption">
                    {enc.image_label
                        ? `${enc.image_label} · ${enc.image_source || ""}`
                        : enc.sketch_url
                            ? "AI Witness Reconstruction · Generated from witness testimony. Not verified evidence."
                            : "Image placeholder. Reconstruction generating…"}
                </p>
                {user && (enc.author_id === user.id || user.role === "admin") && (
                    <ReconstructionTools encounterId={enc.id} onComplete={reload} />
                )}
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-3">
                {enc.source_type === "historical" && (
                    <span className="ok-chip" style={{ color: "var(--ok-accent)", borderColor: "var(--ok-accent)" }} data-testid="detail-historical-badge">
                        <ShieldCheck className="w-3 h-3" /> Verified Historical
                    </span>
                )}
                {enc.source_category && (
                    <span className="ok-chip" data-testid="detail-source-category">
                        {enc.source_category}
                    </span>
                )}
                {(enc.tags || []).slice(0, 6).map((t) => (
                    <span key={t} className="ok-tag" data-testid={`detail-tag-${t}`}>#{t}</span>
                ))}
            </div>

            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1]" data-testid="detail-title">{enc.title}</h1>

            {enc.ai_summary && (
                <p className="text-lg opacity-80 mt-4 leading-relaxed max-w-3xl" data-testid="detail-summary">{enc.ai_summary}</p>
            )}

            {/* Key facts card */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="detail-key-facts">
                <Fact icon={Calendar} label="Date" value={(enc.encounter_date || "").slice(0, 10) || "Unknown"} />
                <Fact icon={MapPin} label="Location" value={enc.location || "Unknown"} />
                <Fact icon={User} label="Witnesses" value={enc.witnesses || "—"} />
                <Fact icon={Tag} label="Type" value={enc.encounter_type || "—"} />
                <Fact icon={Tag} label="Entity" value={enc.entity_known_type || enc.entity_custom_name || "Unknown"} />
                <Fact icon={Tag} label="Craft" value={enc.craft_type || "—"} />
                <Fact icon={ShieldCheck} label="Evidence" value={enc.evidence_level || "—"} />
                <Fact icon={ShieldCheck} label="Reality" value={enc.reality_type || "unknown"} />
            </div>

            {/* Score row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4" data-testid="detail-scores">
                <ScoreCard label="Evidence Index" value={enc.credibility_score ?? 0} max={100} />
                <ScoreCard label="Popularity" value={enc.popularity_score ?? 0} max={100} />
                <div className="ok-card p-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Community Rating</div>
                    <div className="flex items-center gap-3 mt-2">
                        <StarRating value={enc._user_rating || Math.round(enc.rating_avg || 0)} onChange={rate} testId="detail-rate" />
                        <span className="font-mono text-xs opacity-60">{(enc.rating_avg || 0).toFixed(1)} ({enc.rating_count || 0})</span>
                    </div>
                </div>
            </div>

            {/* Engagement bar */}
            <div className="flex items-center gap-3 flex-wrap mt-6 ok-card p-4" data-testid="detail-engagement">
                <VoteButtons
                    targetType="encounter"
                    targetId={enc.id}
                    initialScore={enc.vote_score || 0}
                    initialMyVote={enc.my_vote || 0}
                    layout="horizontal"
                    size="lg"
                />
                <button onClick={toggleLike} className="ok-btn-outline" data-testid="detail-like-btn">
                    <Heart className={`w-4 h-4 ${enc._user_liked ? "fill-[var(--ok-accent)] text-[var(--ok-accent)]" : ""}`} />
                    {enc.like_count || 0}
                </button>
                <AddToCollectionButton targetType="encounter" targetId={enc.id} />
                <div className="ok-chip"><Eye className="w-3 h-3" /> {enc.view_count || 0} views</div>
                <div className="ok-chip"><MessageSquare className="w-3 h-3" /> {comments.length} comments</div>
                <div className="ml-auto flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase opacity-50">Rate evidence</span>
                    {[20, 40, 60, 80, 100].map((s) => (
                        <button key={s} onClick={() => submitCredibility(s)} className="ok-tag hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid={`detail-cred-${s}`}>{s}</button>
                    ))}
                </div>
            </div>

            {/* Encounter DNA / matches callout */}
            {matchCount !== null && matchCount > 0 && (
                <Link to={`/encounters/${id}/matches`} className="ok-card ok-card-hover p-5 mt-6 flex items-center gap-4 group" data-testid="detail-dna-cta">
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: "var(--ok-accent)", color: "var(--ok-on-accent)" }}>
                        <Dna className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Encounter DNA</div>
                        <div className="font-display text-xl mt-0.5">
                            <strong className="text-[var(--ok-accent)]">{matchCount}</strong> similar encounter{matchCount === 1 ? "" : "s"} discovered
                        </div>
                        <div className="text-xs opacity-60 mt-0.5">Compare traits, communications, symptoms and locations.</div>
                    </div>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
            )}

            {/* Full story */}
            <section className="prose prose-invert max-w-none mt-10" data-testid="detail-story">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Full Report</div>
                <div className="text-[15px] leading-7 whitespace-pre-wrap" style={{ color: "var(--ok-text)" }}>
                    <AutoLinkedText text={enc.what_happened} />
                </div>
                {enc.credibility_notes && (
                    <div className="mt-6 ok-card p-4">
                        <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-1">Analyst note</div>
                        <div className="text-sm opacity-80 italic">{enc.credibility_notes}</div>
                    </div>
                )}
            </section>

            {/* Attached media (videos, photographs, visual renditions) merged from media archive */}
            {Array.isArray(enc.media) && enc.media.length > 0 && (
                <section className="mt-12" data-testid="detail-media">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Attached Media</div>
                    <div className="space-y-6">
                        {enc.media.map((m, i) => <CaseMediaPanel key={i} m={m} />)}
                    </div>
                </section>
            )}

            {/* Link to the corresponding media archive entry, if any */}
            {enc.related_media_archive_slug && (
                <Link
                    to={`/media-archive/${enc.related_media_archive_slug}`}
                    className="ok-card ok-card-hover p-5 mt-6 flex items-center gap-4 group"
                    data-testid="detail-media-archive-link"
                >
                    <div className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: "var(--ok-accent)", color: "var(--ok-on-accent)" }}>
                        <Eye className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--ok-accent)]">Media Archive</div>
                        <div className="font-display text-xl mt-0.5">View the full media-archive entry</div>
                        <div className="text-xs opacity-60 mt-0.5">Includes laboratory notes, source provenance and discussion threads specific to the recovered material.</div>
                    </div>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
            )}

            {/* Comments / Witness Discussion — sits directly under the report */}
            <CommentsSection targetType="encounter" targetId={enc.id} />

            {/* Similar cases — below the discussion */}
            {similar.length > 0 && (
                <section className="mt-12" data-testid="detail-similar">
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-3">Similar Cases</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {similar.slice(0, 6).map((s) => <EncounterCard key={s.id} e={s} />)}
                    </div>
                </section>
            )}
        </div>
    );
}

function Fact({ icon: Icon, label, value }) {
    return (
        <div className="ok-card p-3">
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest opacity-50">
                <Icon className="w-3 h-3" />
                {label}
            </div>
            <div className="text-sm font-medium mt-1 truncate">{value}</div>
        </div>
    );
}

function ReconstructionTools({ encounterId, onComplete }) {
    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post(`/encounters/${encounterId}/regenerate-sketch`, { refinement_notes: notes });
            toast.success("Regenerating. Refresh in 20-40s to see the new reconstruction");
            setOpen(false);
            setNotes("");
            setTimeout(onComplete, 30000);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSubmitting(false); }
    };
    return (
        <div className="ml-auto" data-testid="reconstruction-tools">
            {!open ? (
                <button onClick={() => setOpen(true)} className="ok-btn-outline text-xs" data-testid="recon-needs-changes-btn">
                    Needs changes / Regenerate
                </button>
            ) : (
                <div className="ok-card p-3 w-full md:w-[400px] mt-2">
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="What should change? e.g. eyes too large, skin should be darker, craft was triangular not oval…"
                        rows={3}
                        className="ok-input text-sm"
                        data-testid="recon-notes"
                    />
                    <div className="flex gap-2 mt-2">
                        <button onClick={submit} disabled={submitting} className="ok-btn-primary flex-1 justify-center text-xs" data-testid="recon-regenerate-btn">
                            {submitting ? "Queued…" : "Regenerate sketch"}
                        </button>
                        <button onClick={() => setOpen(false)} className="ok-btn-outline text-xs">Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ScoreCard({ label, value, max }) {
    const pct = Math.min(100, Math.round((value / max) * 100));
    return (
        <div className="ok-card p-4">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">{label}</div>
                <div className="font-mono text-sm text-[var(--ok-accent)] font-bold">{value}/{max}</div>
            </div>
            <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ok-surface-hover)" }}>
                <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: "var(--ok-accent)" }} />
            </div>
        </div>
    );
}

function CaseMediaPanel({ m }) {
    const isVideo = m.kind === "video";
    const isImage = m.kind === "image";
    const src = m.embed_url || m.url;
    const fullSrc = (typeof src === "string" && src.startsWith("/api/")) ? staticUrl(src) : src;
    return (
        <div className="ok-card overflow-hidden" data-testid={`case-media-${m.kind || "file"}`}>
            <div className="relative">
                {isVideo ? (
                    <video controls preload="metadata" playsInline className="w-full aspect-video bg-black" src={fullSrc} data-testid="case-media-video">
                        <source src={fullSrc} />
                    </video>
                ) : isImage ? (
                    <img src={fullSrc} alt={m.label || ""} className="w-full max-h-[640px] object-contain" style={{ background: "#000" }} />
                ) : (
                    <a href={fullSrc} target="_blank" rel="noopener noreferrer" className="block py-10 text-center text-[var(--ok-accent)] hover:underline">
                        Open file
                    </a>
                )}
            </div>
            <div className="px-4 py-3 border-t" style={{ borderColor: "var(--ok-border)" }}>
                {m.image_label_kind && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--ok-accent)] mb-1">
                        {m.image_label_kind}
                    </div>
                )}
                {m.label && <div className="text-sm font-semibold">{m.label}</div>}
                {m.caption && <div className="text-xs opacity-80 mt-1 leading-relaxed">{m.caption}</div>}
                {m.source && (
                    <div className="text-[11px] opacity-70 mt-1.5">
                        Source: {m.source_url ? (
                            <a href={m.source_url} target="_blank" rel="noopener noreferrer" className="underline hover:text-[var(--ok-accent)]">{m.source}</a>
                        ) : m.source}
                    </div>
                )}
            </div>
        </div>
    );
}
